import { useState, useEffect } from "react";
import {
  Holiday,
  Municipality,
  fetchHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  importHolidays,
  fetchMunicipalities,
  HolidayCreateData,
} from "@/services/holiday-service";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Download, Edit2, Trash2, RefreshCw } from "lucide-react";
import { Clinic, fetchClinics } from "@/services/clinic-service";

const SCOPE_LABELS: Record<string, string> = {
  NATIONAL: "Nacional",
  STATE: "Estadual",
  MUNICIPAL: "Municipal",
  RECESS: "Recesso",
  CLINIC_RECESS: "Recesso Clinica",
};

const SCOPE_COLORS: Record<string, string> = {
  NATIONAL: "bg-blue-100 text-blue-700",
  STATE: "bg-amber-100 text-amber-700",
  MUNICIPAL: "bg-green-100 text-green-700",
  RECESS: "bg-red-100 text-red-700",
  CLINIC_RECESS: "bg-purple-100 text-purple-700",
};

const Holidays = () => {
  const { toast } = useToast();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importing, setImporting] = useState(false);

  // Filtros
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  const [filterScope, setFilterScope] = useState<string>("");

  // Form state
  const [formName, setFormName] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formScope, setFormScope] = useState("NATIONAL");
  const [formStateCode, setFormStateCode] = useState("");
  const [formCityName, setFormCityName] = useState("");
  const [formClinicId, setFormClinicId] = useState("");

  useEffect(() => {
    loadHolidays();
    loadMunicipalities("RJ");
    loadClinics();
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [filterYear, filterScope]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const data = await fetchHolidays(
        filterScope && filterScope !== "ALL" ? filterScope : undefined,
        undefined,
        undefined,
        filterYear || undefined,
      );
      setHolidays(data);
    } catch {
      toast({ title: "Erro ao carregar feriados", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadClinics = async () => {
    try {
      setLoadingClinics(true);
      const data = await fetchClinics(undefined, true, true);
      setClinics(data || []);
    } catch {
      toast({ title: "Erro ao carregar clínicas", variant: "destructive" });
    } finally {
      setLoadingClinics(false);
    }
  };

  const loadMunicipalities = async (state?: string) => {
    try {
      const data = await fetchMunicipalities(state);
      setMunicipalities(data);
    } catch {
      toast({ title: "Erro ao carregar municípios", variant: "destructive" });
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await importHolidays(importYear);
      toast({
        title: `Importacao concluida`,
        description: `${result.created} feriados criados, ${result.skipped} ja existiam`,
      });
      loadHolidays();
    } catch (e: any) {
      toast({ title: e.message || "Erro ao importar", variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedHoliday(null);
    setFormName("");
    setFormDate("");
    setFormScope("NATIONAL");
    setFormStateCode("");
    setFormCityName("");
    setFormClinicId("");
    setDialogOpen(true);
  };

  const openEditDialog = (h: Holiday) => {
    setSelectedHoliday(h);
    setFormName(h.name);
    setFormDate(h.holiday_date);
    setFormScope(h.scope);
    setFormStateCode(h.state_code);
    setFormCityName(h.city_name);
    setFormClinicId(h.clinic_id || "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formDate) {
      toast({ title: "Preencha nome e data", variant: "destructive" });
      return;
    }

    const data: HolidayCreateData = {
      name: formName,
      holiday_date: formDate,
      scope: formScope,
      state_code: (formScope === "STATE" || formScope === "MUNICIPAL") ? formStateCode : "",
      city_name: formScope === "MUNICIPAL" ? formCityName : "",
      clinic_id: formScope === "CLINIC_RECESS" ? formClinicId : null,
    };

    try {
      if (selectedHoliday) {
        await updateHoliday(selectedHoliday.id, data);
        toast({ title: "Feriado atualizado" });
      } else {
        await createHoliday(data);
        toast({ title: "Feriado criado" });
      }
      setDialogOpen(false);
      loadHolidays();
    } catch (e: any) {
      toast({ title: e.message || "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!selectedHoliday) return;
    try {
      await deleteHoliday(selectedHoliday.id);
      toast({ title: "Feriado excluido" });
      setDeleteDialogOpen(false);
      setSelectedHoliday(null);
      loadHolidays();
    } catch (e: any) {
      toast({ title: e.message || "Erro ao excluir", variant: "destructive" });
    }
  };

  const filteredMunicipalities = formStateCode
    ? municipalities.filter(m => m.state_code === formStateCode)
    : municipalities;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Feriados</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadHolidays}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={openCreateDialog}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Novo Feriado
          </Button>
        </div>
      </div>

      {/* Filtros + Import */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <Label className="text-xs">Ano</Label>
          <Input
            type="number"
            className="h-8 w-24 text-sm"
            value={filterYear}
            onChange={e => setFilterYear(Number(e.target.value))}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Escopo</Label>
          <Select value={filterScope} onValueChange={setFilterScope}>
            <SelectTrigger className="h-8 w-40 text-sm">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="NATIONAL">Nacional</SelectItem>
              <SelectItem value="STATE">Estadual</SelectItem>
              <SelectItem value="MUNICIPAL">Municipal</SelectItem>
              <SelectItem value="RECESS">Recesso</SelectItem>
              <SelectItem value="CLINIC_RECESS">Recesso Clinica</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Importar da BrasilAPI</Label>
            <div className="flex gap-1">
              <Input
                type="number"
                className="h-8 w-24 text-sm"
                value={importYear}
                onChange={e => setImportYear(Number(e.target.value))}
              />
              <Button size="sm" className="h-8" onClick={handleImport} disabled={importing}>
                <Download className="h-3.5 w-3.5 mr-1" />
                {importing ? "Importando..." : "Importar"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Data</TableHead>
              <TableHead className="text-xs">Nome</TableHead>
              <TableHead className="text-xs">Escopo</TableHead>
              <TableHead className="text-xs">UF</TableHead>
              <TableHead className="text-xs">Cidade</TableHead>
              <TableHead className="text-xs">Clinica</TableHead>
              <TableHead className="text-xs w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : holidays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                  Nenhum feriado encontrado
                </TableCell>
              </TableRow>
            ) : (
              holidays.map(h => (
                <TableRow key={h.id}>
                  <TableCell className="text-xs font-mono">
                    {(() => { const [,m,d] = h.holiday_date.split("-"); return `${d}/${m}`; })()} ({new Date(h.holiday_date + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "short" })})
                  </TableCell>
                  <TableCell className="text-xs font-medium">{h.name}</TableCell>
                  <TableCell className="text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      SCOPE_COLORS[h.scope] || "bg-gray-100 text-gray-700"
                    }`}>
                      {SCOPE_LABELS[h.scope] || h.scope}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{h.state_code || "-"}</TableCell>
                  <TableCell className="text-xs">{h.city_name || "-"}</TableCell>
                  <TableCell className="text-xs">
                    {h.clinic_id ? (clinics.find(c => c.id === h.clinic_id)?.trade_name || clinics.find(c => c.id === h.clinic_id)?.name || "Nao encontrada") : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(h)}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => { setSelectedHoliday(h); setDeleteDialogOpen(true); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedHoliday ? "Editar Feriado" : "Novo Feriado"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Nome *</Label>
              <Input value={formName} onChange={e => setFormName(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data *</Label>
              <Input type="date" value={formDate} onChange={e => setFormDate(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Escopo *</Label>
              <Select value={formScope} onValueChange={v => { setFormScope(v); if (v === "NATIONAL" || v === "RECESS" || v === "CLINIC_RECESS") { setFormStateCode(""); setFormCityName(""); } }}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NATIONAL">Nacional</SelectItem>
                  <SelectItem value="STATE">Estadual</SelectItem>
                  <SelectItem value="MUNICIPAL">Municipal</SelectItem>
                  <SelectItem value="RECESS">Recesso (todas)</SelectItem>
                  <SelectItem value="CLINIC_RECESS">Recesso Clinica</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formScope === "STATE" || formScope === "MUNICIPAL") && (
              <div className="space-y-1">
                <Label className="text-xs">UF *</Label>
                <Select value={formStateCode} onValueChange={v => { setFormStateCode(v); setFormCityName(""); loadMunicipalities(v); }}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formScope === "CLINIC_RECESS" && (
              <div className="space-y-1">
                <Label className="text-xs">Clinica *</Label>
                <Select value={formClinicId} onValueChange={setFormClinicId}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder={loadingClinics ? "Carregando..." : "Selecione a clinica"} />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingClinics ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Carregando clinicas...</div>
                    ) : clinics.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Nenhuma clinica encontrada</div>
                    ) : (
                      clinics.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.trade_name || c.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formScope === "MUNICIPAL" && formStateCode && (
              <div className="space-y-1">
                <Label className="text-xs">Municipio *</Label>
                <Select value={formCityName} onValueChange={setFormCityName}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Selecione o municipio" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredMunicipalities.map(m => (
                      <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                    ))}
                    {filteredMunicipalities.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">
                        Nenhum municipio cadastrado para {formStateCode}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleSave}>
              {selectedHoliday ? "Salvar" : "Criar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Exclusao */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir feriado?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{selectedHoliday?.name}"? Esta acao nao pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Holidays;
